import { TrackedLinkService, TrackedLink, LinkAnalytics, LinkClick } from '@app/_components/tracked-link';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Component, OnInit, Inject } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MessageService } from 'primeng/api';
import { first } from 'rxjs/operators';
import { MsgService } from '@app/_components/msg';
import { InboxComponent } from '@app/_components/inbox/inbox/inbox.component';

@Component({
    selector: 'app-link-analytics',
    templateUrl: './link-analytics.component.html',
    styleUrls: ['./link-analytics.component.css'],
    providers: [MessageService],
    standalone: false
})
export class LinkAnalyticsComponent implements OnInit {

	trackedLink: TrackedLink;
	analytics: LinkAnalytics;
	clicks: LinkClick[] = [];
	loading = true;
	clicksLoading = false;

	// Click detail pagination
	clickPage = 0;
	clickSize = 20;
	clickTotal = 0;

	// Device breakdown for display
	deviceEntries: { device: string, count: number, percent: number }[] = [];

	constructor(
		private trackedLinkService: TrackedLinkService,
		private clipboard: Clipboard,
		private messageService: MessageService,
		private dialog: MatDialog,
		private msgService: MsgService,
		@Inject(MAT_DIALOG_DATA) data: any
	) {
		this.trackedLink = data.trackedLink;
	}

	ngOnInit() {
		this.loadAnalytics();
		this.loadClicks();
	}

	loadAnalytics() {
		this.loading = true;
		this.trackedLinkService.getLinkAnalytics(this.trackedLink.trackedLinkId).pipe(first()).subscribe(
			(res: any) => {
				this.analytics = res.data.analytics;
				this.buildDeviceEntries();
			},
			(err: any) => { console.log('Analytics error: ' + JSON.stringify(err)); },
			() => { this.loading = false; }
		);
	}

	loadClicks() {
		this.clicksLoading = true;
		this.trackedLinkService.getLinkClicks(this.trackedLink.trackedLinkId, this.clickPage, this.clickSize).pipe(first()).subscribe(
			(res: any) => {
				this.clicks = res.data.clicks || [];
				this.clickTotal = res.data.clicks?.length || 0;
			},
			(err: any) => { console.log('Clicks error: ' + JSON.stringify(err)); },
			() => { this.clicksLoading = false; }
		);
	}

	buildDeviceEntries() {
		if (!this.analytics?.deviceBreakdown) { return; }
		const breakdown = this.analytics.deviceBreakdown;
		const total = Object.values(breakdown).reduce((a, b) => a + b, 0);
		this.deviceEntries = Object.entries(breakdown).map(([device, count]) => ({
			device: device,
			count: count,
			percent: total > 0 ? Math.round((count / total) * 100) : 0
		})).sort((a, b) => b.count - a.count);
	}

	copyTrackingUrl() {
		this.clipboard.copy(this.analytics?.trackingUrl || this.trackedLink.trackingUrl);
		this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'Tracking URL copied', life: 2000 });
	}

	getDeviceIcon(device: string): string {
		switch (device) {
			case 'DESKTOP': return 'pi pi-desktop';
			case 'MOBILE': return 'pi pi-mobile';
			case 'TABLET': return 'pi pi-tablet';
			case 'BOT': return 'pi pi-cog';
			default: return 'pi pi-question-circle';
		}
	}

	getMaxClicks(): number {
		if (!this.analytics?.clicksByDay || this.analytics.clicksByDay.length === 0) { return 1; }
		return Math.max(...this.analytics.clicksByDay.map(d => d.clicks), 1);
	}

	openMessage(msgId: number) {
		if (!msgId) { return; }
		this.msgService.findById(msgId).pipe(first()).subscribe(
			(res: any) => {
				const msg = res.data.msg;
				if (!msg) {
					this.messageService.add({ severity: 'warn', summary: 'Not Found', detail: 'Message ' + msgId + ' not found.', life: 3000 });
					return;
				}
				const dialogConfig = new MatDialogConfig();
				dialogConfig.width = '100%';
				dialogConfig.height = '100%';
				dialogConfig.maxWidth = '1600px';
				dialogConfig.maxHeight = '800px';
				dialogConfig.disableClose = false;
				dialogConfig.autoFocus = true;
				dialogConfig.hasBackdrop = true;
				dialogConfig.data = { inbox: msg, title: 'View Message' };
				this.dialog.open(InboxComponent, dialogConfig);
			},
			(err: any) => {
				this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load message ' + msgId, life: 3000 });
			}
		);
	}
}
